import { capitalize } from 'lodash-es'

import { exists, getFirst } from '#imports'

export const STORE_TYPE = {
  MONOBRAND: 'monobrand',
  MULTIBRAND: 'multibrand'
}

const ICONS = {
  MONOBRAND: '/images/svgs/monobrand.svg',
  MULTIBRAND: '/images/svgs/multibrand.svg'
}

const ICONS_MINIMAL = {
  MONOBRAND: '/images/svgs/monobrand-min.svg',
  MULTIBRAND: '/images/svgs/multibrand-min.svg'
}

export const getStoreType = store => {
  // TODO: THIS IS ONLY TO TEST
  if (store.isSpecialistSingleBrand) return STORE_TYPE.MONOBRAND
  return STORE_TYPE.MULTIBRAND
}

export const getStoreIconUrl = store => {
  return getStoreType(store) === STORE_TYPE.MONOBRAND ? ICONS.MONOBRAND : ICONS.MULTIBRAND
}

export const getStoreIconUrlMinimal = store => {
  return getStoreType(store) === STORE_TYPE.MONOBRAND ? ICONS_MINIMAL.MONOBRAND : ICONS_MINIMAL.MULTIBRAND
}

export const distance = distanceInKm => {
  if (!distanceInKm) return ''
  return parseFloat(distanceInKm).toFixed(2)
}

// Costruisce l'URL "Ottieni indicazioni": usa il campo CMS "Google Maps Url"
// se valorizzato, altrimenti ripiega sulle coordinate del campo mappa.
export const buildDirectionsUrl = store => {
  if (store?.googleMapsUrl) return store.googleMapsUrl

  const [ lat, lng ] = store?.location?.coordinates?.split(',') ?? []
  if (!lat || !lng) return null

  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

export const storeAddress = store => {
  const parts = [ capitalize(store.storeAddress) ]

  const cityParts = [ store.zipCode, capitalize(store.city) ].filter(Boolean).join(' ')
  let location = cityParts

  const provinceTitle = getFirst(store.province)?.title
  if (provinceTitle) {
    location += ` (${provinceTitle})`
  }

  if (location) parts.push(location)

  // If we have a country that is NOT Italy, or if we just want to avoid hardcoding it.
  // The user request is "remove 'Italia' at the end but it's not correct".
  // This implies for abroad stores it says Italia.
  // If we just remove it, it works for everyone (Italy is implied for local users, or acceptable).
  // Or we can try to append store.country if it exists.
  // if (store.country && store.country.toLowerCase() !== 'it' && store.country.toLowerCase() !== 'italy') {
  //   parts.push(store.country)
  // }

  return parts.join(',\n  ')
}

export const getStoreDisplayName = store => {
  if (!store) return ''
  if (store.storeDisplayName) return store.storeDisplayName

  const isMonobrand = !!store.isSpecialistSingleBrand
  const fallbackName = store.storeName || store.title || ''

  if (!isMonobrand) {
    return fallbackName
  }

  const subtitle = (store.logoSubtitle || '').trim()
  if (subtitle) {
    return `Veneta Cucine ${subtitle}`.trim()
  }

  const legal = (store.storeLegalName || '').trim()
  if (legal) {
    const hasVenetaPrefix = /veneta\s+cucine/i.test(legal)
    if (hasVenetaPrefix) {
      const rest = legal.replace(/veneta\s+cucine/i, '').trim()
      return rest ? `Veneta Cucine ${rest}` : 'Veneta Cucine'
    }
    return legal
  }

  return fallbackName
}

export const getStoreDisplayTitleWithLocation = store => {
  if (!store) return ''

  const baseName = store.storeDisplayName || getStoreDisplayName(store)

  const city = (store?.city || '').trim()
  const province = (getFirst(store?.province)?.title || '').trim()

  // Resolve foreign country, handling both object and array shapes
  const country = getFirst(store?.foreignCountry)?.title
  const isForeign = !!country && !/^(it|italia|italy)$/i.test(country)

  // ITALY (or unknown country): keep original city/province logic
  if (!isForeign) {
    if (!city && !province) return baseName

    const cityLower = city.toLowerCase()
    const provinceLower = province.toLowerCase()
    const nameLower = baseName.toLowerCase()

    // city == province -> only province
    if (city && province && cityLower === provinceLower) {
      if (!nameLower.includes(provinceLower)) {
        return `${baseName} (${province})`
      }
      return baseName
    }

    // city != province -> "(city, province)" if province not already in the name
    if (city && province) {
      if (!nameLower.includes(provinceLower)) {
        return `${baseName} (${city}, ${province})`
      }
      return baseName
    }

    // Only city
    if (city && !nameLower.includes(cityLower)) {
      return `${baseName} (${city})`
    }

    // Only province
    if (province && !nameLower.includes(provinceLower)) {
      return `${baseName} (${province})`
    }

    return baseName
  }

  // FOREIGN: "<name> (address)" where address is "city, country" or just one of them
  const parts = []
  if (city) parts.push(city)
  if (country) parts.push(country)

  const address = parts.join(', ').trim()
  if (!address) return baseName

  const nameLower = baseName.toLowerCase()
  const addressLower = address.toLowerCase()
  if (nameLower.includes(addressLower)) {
    return baseName
  }

  return `${baseName} (${address})`
}

export const getStoreSearchDisplayTitle = store => {
  if (!store) return ''

  const baseName = store.storeDisplayName || getStoreDisplayName(store)

  const city = (store?.city || '').trim()

  // Resolve foreign country, handling both object and array shapes
  const country = getFirst(store?.foreignCountry)?.title

  const isForeign = !!country && !/^(it|italia|italy)$/i.test(country)

  // For Italian (or generic non-foreign) stores, reuse the standard helper
  if (!isForeign) {
    return getStoreDisplayTitleWithLocation(store)
  }

  // Foreign stores: "<name> (<nation>, address)"
  const nation = country
  const hasNation = !!nation
  const hasCity = !!city

  if (!hasNation && !hasCity) {
    return baseName
  }

  if (hasNation && hasCity) {
    return `${baseName} (${nation}, ${city})`
  }

  if (hasNation) {
    return `${baseName} (${nation})`
  }

  // Fallback: only city
  return `${baseName} (${city})`
}
